import type { UserSummary } from '@blackblack/shared';
import { can } from '@blackblack/shared';
import { api } from '../../lib/api';
import { useQuery } from '../../lib/query';
import { navigate } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { player } from '../../state/player';
import { useToast } from '../../state/toast';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Avatar, Button } from '../../components/ui/primitives';
import { Sheet, useOpenSheet } from '../../components/ui/Sheet';
import { Page, PageHeader } from '../../components/layout';
import { Wordmark } from '../../components/ui/Logo';
import styles from './profile.module.css';

function Item({ icon, label, detail, onClick, danger }: { icon: IconName; label: string; detail?: string; onClick: () => void; danger?: boolean }) {
  return (
    <li>
      <button className={`${styles.settingsItem} ${danger ? styles.danger : ''}`} onClick={onClick}>
        <Icon name={icon} size={20} />
        {label}
        {detail && <small>{detail}</small>}
        {!danger && <Icon name="right" size={16} style={{ marginLeft: detail ? 0 : 'auto', color: 'var(--text-3)' }} />}
      </button>
    </li>
  );
}

export function SettingsPage() {
  const { me, logout } = useAuth();
  const toast = useToast();
  const openSheet = useOpenSheet();
  if (!me) return null;

  const showBlocked = () => openSheet((close) => <BlockedSheet onClose={close} />);

  return (
    <Page>
      <PageHeader title="Instellingen" fallback="/me" />
      <h2 className={styles.settingsGroup}>Account</h2>
      <ul className={styles.settingsList}>
        <Item icon="edit" label="Profiel bewerken" onClick={() => navigate('/settings/profile')} />
        <Item icon="user" label="E-mail" detail={me.email} onClick={() => toast('E-mail wijzigen komt binnenkort')} />
        <Item icon="lock" label="Wachtwoord wijzigen" onClick={() => navigate('/forgot-password')} />
      </ul>
      <h2 className={styles.settingsGroup}>Inkomsten</h2>
      <ul className={styles.settingsList}>
        <Item icon="sparkle" label="Creator Pro & fan-steun" {...(me.pro ? { detail: 'Pro' } : {})} onClick={() => navigate('/settings/billing')} />
      </ul>
      <h2 className={styles.settingsGroup}>Privacy & veiligheid</h2>
      <ul className={styles.settingsList}>
        <Item icon="ban" label="Geblokkeerde accounts" onClick={showBlocked} />
        <Item icon="bell" label="Meldingen" onClick={() => navigate('/notifications')} />
      </ul>
      <h2 className={styles.settingsGroup}>Over BlackBlack</h2>
      <ul className={styles.settingsList}>
        <Item icon="note" label="Voorwaarden" onClick={() => navigate('/voorwaarden')} />
        <Item icon="shield" label="Privacybeleid" onClick={() => navigate('/privacy')} />
      </ul>
      {can(me.role, 'admin.access') && (
        <>
          <h2 className={styles.settingsGroup}>Beheer</h2>
          <ul className={styles.settingsList}>
            <Item icon="shield" label="Admin dashboard" detail={me.role} onClick={() => navigate('/admin')} />
          </ul>
        </>
      )}
      <h2 className={styles.settingsGroup}>Sessie</h2>
      <ul className={styles.settingsList}>
        <Item
          icon="logout"
          label="Uitloggen"
          danger
          onClick={async () => {
            player.stop();
            await logout();
            navigate('/', { replace: true });
            toast('Je bent uitgelogd');
          }}
        />
      </ul>
      <p className={styles.version}>
        <Wordmark size={14} /> · versie 0.1.0
      </p>
    </Page>
  );
}

function BlockedSheet({ onClose }: { onClose: () => void }) {
  const q = useQuery<UserSummary[]>('blocked', () => api.get('/users/me/blocked'), { staleMs: 0 });
  const toast = useToast();
  return (
    <Sheet open onClose={onClose} title="Geblokkeerde accounts">
      <div style={{ padding: '0 8px 20px' }}>
        {q.data?.length === 0 && <p style={{ padding: 16, color: 'var(--text-3)' }}>Je hebt niemand geblokkeerd.</p>}
        {q.data?.map((u) => (
          <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8 }}>
            <Avatar user={u} size={40} />
            <span style={{ flex: 1, fontWeight: 700 }}>@{u.username}</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                await api.delete(`/users/${u.id}/block`);
                toast(`@${u.username} gedeblokkeerd`);
                void q.refetch();
              }}
            >
              Deblokkeren
            </Button>
          </div>
        ))}
      </div>
    </Sheet>
  );
}
